import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JsBarcode from 'jsbarcode';
import { formatItemDisplayName } from './formatItemDisplayName';
import {
  generatePdfFromHtmlElement,
  renderTemplateOffscreenAndGeneratePdf,
  sharePdfArrayBuffer,
} from '../../../shared/utils/htmlTemplateToPdf';

/**
 * Generate a PNG data URL for a barcode.
 */
function generateBarcodeDataUrl(text) {
  if (!text) return null;
  try {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, String(text).trim(), {
      format: 'CODE128',
      width: 2,
      height: 36,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
}

/**
 * Format date for display (dd/MM/yyyy).
 */
function formatDate(dateValue, timeValue, dateFormat = 'dd/MM/yyyy') {
  if (!dateValue) return '';
  const dDate = new Date(dateValue);
  if (Number.isNaN(dDate.getTime())) return '';
  const dTime = timeValue ? new Date(timeValue) : dDate;
  const dd = String(dDate.getDate()).padStart(2, '0');
  const mm = String(dDate.getMonth() + 1).padStart(2, '0');
  const yyyy = dDate.getFullYear();
  let hours = dTime.getHours();
  const minutes = String(dTime.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const timeStr = `${hours}:${minutes} ${ampm}`;
  let dateStr = '';
  switch (dateFormat) {
    case 'MM/dd/yyyy':
      dateStr = `${mm}/${dd}/${yyyy}`;
      break;
    case 'yyyy-MM-dd':
      dateStr = `${yyyy}-${mm}-${dd}`;
      break;
    case 'dd-MM-yyyy':
      dateStr = `${dd}-${mm}-${yyyy}`;
      break;
    case 'dd/MM/yyyy':
    default:
      dateStr = `${dd}/${mm}/${yyyy}`;
  }
  return `${dateStr} ${timeStr}`;
}

/**
 * Fetch image from URL and return as data URL (for embedding in PDF).
 */
async function fetchLogoAsDataUrl(url) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Export a sales order (invoice) to PDF matching the selected template.
 * @param {Object} order - Sales order data
 * @param {Object} company - Company data
 * @param {Object} options - Export options (element, templateKey, filename, save, etc.)
 */
export async function exportSalesOrderToPdf(order, company, options = {}) {
  const {
    element,
    templateKey = company?.salesPrintTemplate || 'custom',
    filename = (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
    save = true,
    showTax = true,
    itemFormat = company?.printItemNameFormat || 'name_sku',
    currency = company?.currency || 'USD',
    dateFormat = company?.dateFormat || 'dd/MM/yyyy',
  } = options;

  // 1. If an existing DOM element was provided (e.g. from SalesOrderPrint preview), capture it directly
  if (element) {
    try {
      return await generatePdfFromHtmlElement(element, {
        templateKey,
        paperType: templateKey === 'thermal' ? 'thermal' : (templateKey === 'a5' || templateKey === 'a5_preprinted' || company?.printPaperType === 'a5' ? 'a5' : 'a4'),
        filename,
        save,
        scale: 2,
      });
    } catch (err) {
      console.warn('Direct element PDF capture failed, falling back to offscreen render:', err);
    }
  }

  // 2. Render template in headless container and generate PDF
  try {
    return await renderTemplateOffscreenAndGeneratePdf({
      docType: 'invoice',
      order,
      company,
      templateKey,
      showTax,
      currency,
      itemFormat,
      dateFormat,
      filename,
      save,
    });
  } catch (err) {
    console.warn('Offscreen HTML-to-PDF generation failed, falling back to legacy jsPDF generator:', err);
    return await exportSalesOrderToPdfLegacy(order, company, options);
  }
}

/**
 * Legacy jsPDF generator used as fallback if canvas capture is not available.
 */
export async function exportSalesOrderToPdfLegacy(order, company, options = {}) {
  let logoDataUrl = options.logoDataUrl;
  if (!logoDataUrl && company?.logoUrl) {
    logoDataUrl = await fetchLogoAsDataUrl(company.logoUrl);
  }

  const {
    filename = (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
    itemFormat = company?.printItemNameFormat || 'name_sku',
    save = true,
  } = options;

  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value ?? 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (company?.currencyDecimals ?? 2), maximumFractionDigits: (company?.currencyDecimals ?? 2)}).format(amount);
    } catch {
      return `${currency} ${amount.toFixed((company?.currencyDecimals ?? 2))}`;
    }
  };

  const formatItemName = (item) => {
    let name = formatItemDisplayName(
      {
        name: item.product?.name || item.productName,
        sku: item.product?.sku || item.productSku,
        brand: item.product?.brand || item.productBrand,
        description: item.product?.description || item.description || item.notes,
      },
      itemFormat
    );

    const showWarranty = company?.showWarrantyOnInvoice !== false;

    if (showWarranty) {
      if (item.hasWarranty && item.warrantyPeriod) {
        name += `\nWarranty: ${item.warrantyPeriod.name}`;
      }
      
      let serials = item.serialNumbers;
      if (typeof serials === 'string') {
        try {
          serials = JSON.parse(serials);
        } catch (e) {
          serials = null;
        }
      }

      if (serials && Array.isArray(serials) && serials.length > 0) {
        const validSerials = serials.filter(s => s);
        if (validSerials.length > 0) {
          name += `\nSN: ${validSerials.join(', ')}`;
        }
      }
    }

    return name;
  };

  const templateKey = company?.salesPrintTemplate || 'standard';
  const isA5 = templateKey === 'a5' || templateKey === 'a5_preprinted' || company?.printPaperType === 'a5';
  const isPreprinted = templateKey === 'a4_preprinted' || templateKey === 'a5_preprinted';
  const pdfFormat = isA5 ? 'a5' : 'a4';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: pdfFormat });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = isA5 ? 10 : 14;

  const colorPrimary = isA5 ? [26, 37, 44] : [15, 23, 42];
  const colorAccent = isA5 ? [78, 205, 196] : [37, 99, 235];
  const colorTextDark = [30, 41, 59];
  const colorTextMuted = [100, 116, 139];
  const colorBgCard = [248, 250, 252];
  const colorBorder = [226, 232, 240];

  let y = isPreprinted ? 50 : (isA5 ? 10 : 14);

  if (!isPreprinted) {
    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(0, 0, pageWidth, isA5 ? 2 : 3, 'F');
  }

  const logoSize = isA5 ? 14 : 18;
  const logoPadding = isA5 ? 1.5 : 2;
  
  if (!isPreprinted) {
    try {
      const format = logoDataUrl?.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      if (logoDataUrl) {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, logoSize + logoPadding * 2, logoSize + logoPadding * 2, 1.5, 1.5, 'FD');
        doc.addImage(logoDataUrl, format, margin + logoPadding, y + logoPadding, logoSize, logoSize);
      }
    } catch (e) {
      // ignore logo error
    }
  }

  let companyInfoY = y + (isA5 ? 7 : 9);
  const companyX = logoDataUrl ? margin + logoSize + logoPadding * 2 + 4 : margin;
  
  if (!isPreprinted) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isA5 ? 11 : 16);
    doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.text(company?.name || 'Company Name', companyX, y + (isA5 ? 3 : 4));
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(isA5 ? 7.5 : 8.5);
    doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
    
    const companyTextWidth = isA5 ? 65 : pageWidth * 0.45;
    if (company?.address) {
      const addrLines = doc.splitTextToSize(company.address, companyTextWidth);
      addrLines.forEach((line) => {
        doc.text(line, companyX, companyInfoY);
        companyInfoY += isA5 ? 3.5 : 4;
      });
    }

    if (company?.trn) {
      doc.setFont('helvetica', 'bold');
      doc.text(`TRN: ${company.trn}`, companyX, companyInfoY);
      companyInfoY += isA5 ? 3.5 : 4;
    }
  }

  if (isA5) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.text('INVOICE', pageWidth - margin, y + 3, { align: 'right' });

    doc.setFontSize(8);
    let metaY = y + 8;
    
    const drawMetaRowA5 = (label, value) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
      doc.text(label, pageWidth - margin - 28, metaY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);
      doc.text(value, pageWidth - margin, metaY, { align: 'right' });
      metaY += 4;
    };

    drawMetaRowA5('No:', order.invoiceNumber || order.orderNumber || '-');
    const barcodeDataUrlA5 = generateBarcodeDataUrl(order.invoiceNumber || order.orderNumber);
    if (barcodeDataUrlA5) {
      doc.addImage(barcodeDataUrlA5, 'PNG', pageWidth - margin - 35, metaY, 35, 5.5);
      metaY += 6.5;
    }
    drawMetaRowA5('Payment:', ((ord) => {
      if (ord.salesType === 'credit') return 'CREDIT';
      if (ord.payments && Array.isArray(ord.payments) && ord.payments.length > 0) {
        return Array.from(new Set(ord.payments.map((p) => (p.method ? p.method.toUpperCase() : 'CASH')))).join(', ');
      }
      return (ord.salesType || 'CASH').toUpperCase();
    })(order));

    if (order.sourceQuotation?.quotationNumber) {
      drawMetaRowA5('Ref Quot:', order.sourceQuotation.quotationNumber);
    }
    
    y = Math.max(companyInfoY + 4, metaY + 4);
  } else {
    const metaCardWidth = 65;
    const metaCardX = pageWidth - margin - metaCardWidth;
    const metaCardY = y;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.text('TAX INVOICE', pageWidth - margin, y + 4, { align: 'right' });

    const barcodeDataUrl = generateBarcodeDataUrl(order.invoiceNumber || order.orderNumber);
    let metaRows = 3;
    if (order.sourceQuotation?.quotationNumber) metaRows += 1;
    const barcodeHeight = barcodeDataUrl ? 7.5 : 0;
    const metaCardHeight = 4 + (metaRows * 4.5) + barcodeHeight + 3;

    doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(metaCardX, metaCardY + 7, metaCardWidth, metaCardHeight, 2, 2, 'FD');
    
    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(metaCardX, metaCardY + 7, 1.5, metaCardHeight, 'F');

    let metaY = metaCardY + 11.5;
    doc.setFontSize(8);
    
    const drawMetaRow = (label, value) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
      doc.text(label, metaCardX + 4, metaY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);
      doc.text(value, pageWidth - margin - 4, metaY, { align: 'right' });
      metaY += 4.5;
    };

    drawMetaRow('Invoice No:', order.invoiceNumber || order.orderNumber || '-');
    if (barcodeDataUrl) {
      doc.addImage(barcodeDataUrl, 'PNG', metaCardX + 4, metaY, metaCardWidth - 8, 6);
      metaY += 7.5;
    }
    drawMetaRow('Date:', formatDate(order.invoiceDate, order.createdAt, dateFormat));
    drawMetaRow('Payment Method:', ((ord) => {
      if (ord.salesType === 'credit') return 'CREDIT';
      if (ord.payments && Array.isArray(ord.payments) && ord.payments.length > 0) {
        return Array.from(new Set(ord.payments.map((p) => (p.method ? p.method.toUpperCase() : 'CASH')))).join(', ');
      }
      return (ord.salesType || 'CASH').toUpperCase();
    })(order));

    if (order.sourceQuotation?.quotationNumber) {
      drawMetaRow('Ref Quotation:', order.sourceQuotation.quotationNumber);
    }

    y = Math.max(companyInfoY + 6, metaCardY + 7 + metaCardHeight + 6);
  }

  if (!isA5) {
    const billToWidth = pageWidth - margin * 2;
    const billToHeight = 28;
    doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, billToWidth, billToHeight, 2, 2, 'FD');

    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(margin, y, 1.5, billToHeight, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.text('BILL TO', margin + 6, y + 6);

    let clientY = y + 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.text(order.customer?.name || '-', margin + 6, clientY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);

    let clientInfoStr = '';
    if (order.customer?.address) {
      clientInfoStr += `${order.customer.address}  |  `;
    }
    if (order.customer?.trn) {
      clientInfoStr += `TRN: ${order.customer.trn}`;
    }
    if (clientInfoStr.endsWith('  |  ')) {
      clientInfoStr = clientInfoStr.slice(0, -5);
    }
    
    clientY += 5;
    const clientInfoLines = doc.splitTextToSize(clientInfoStr || '-', billToWidth - 12);
    doc.text(clientInfoLines, margin + 6, clientY);

    y += billToHeight + 8;
  } else {
    doc.setDrawColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
  }

  const items = order.items || [];
  const tableHeaders = isA5
    ? ['#', 'Item Description', 'Qty', 'Price', 'Total']
    : ['#', 'Item', 'Qty', 'Unit', 'Price', 'Discount', 'VAT', 'Line Total'];

  const tableRows = items.map((it, idx) => {
    if (isA5) {
      return [
        String(idx + 1),
        formatItemName(it),
        Number(it.quantity ?? 0).toFixed(company?.currencyDecimals ?? 2),
        formatAmount(it.unitPrice),
        formatAmount(it.total ?? 0),
      ];
    }
    return [
      String(idx + 1),
      formatItemName(it),
      Number(it.quantity ?? 0).toFixed(company?.currencyDecimals ?? 2),
      it.unit || 'pcs',
      formatAmount(it.unitPrice),
      formatAmount(it.discount ?? 0),
      formatAmount(it.vatAmount ?? 0),
      formatAmount(it.total ?? 0),
    ];
  });

  const columnStyles = isA5
    ? {
        0: { halign: 'center', cellWidth: 8 },
        1: { halign: 'left' },
        2: { halign: 'right', cellWidth: 15 },
        3: { halign: 'right', cellWidth: 20 },
        4: { halign: 'right', cellWidth: 22 },
      }
    : {
        0: { halign: 'center', cellWidth: 10 },
        1: { halign: 'left' },
        2: { halign: 'right', cellWidth: 20 },
        3: { halign: 'center', cellWidth: 15 },
        4: { halign: 'right', cellWidth: 24 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'right', cellWidth: 20 },
        7: { halign: 'right', cellWidth: 26 },
      };

  autoTable(doc, {
    startY: y,
    head: [tableHeaders],
    body: tableRows,
    margin: { 
      top: isPreprinted ? 55 : 15,
      bottom: isPreprinted ? 25 : 15,
      left: margin, 
      right: margin 
    },
    styles: { fontSize: isA5 ? 7.5 : 8, font: 'helvetica', cellPadding: isA5 ? 2.5 : 3.5, textColor: colorTextDark },
    headStyles: { fillColor: isA5 ? [78, 205, 196] : colorPrimary, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: isA5 ? [250, 253, 252] : [252, 253, 254] },
    tableLineColor: colorBorder,
    tableLineWidth: 0.1,
    columnStyles: columnStyles,
  });

  let cardRows = 4;
  if (Number(order.roundOff)) cardRows += 1;
  const totalsCardHeight = 4 + (cardRows * (isA5 ? 4.2 : 5)) - 1;

  const targetY = pageHeight - (isPreprinted ? 25 : 16) - totalsCardHeight;
  y = doc.lastAutoTable.finalY + 6;

  if (y > targetY) {
    doc.addPage();
    if (!isPreprinted) {
      doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
      doc.rect(0, 0, pageWidth, isA5 ? 2 : 3, 'F');
    }
    y = targetY;
  } else {
    y = targetY;
  }

  const leftColWidth = isA5 ? 60 : pageWidth * 0.45;
  let notesY = y;
  if (order.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isA5 ? 8 : 9);
    doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.text('Notes & Terms', margin, notesY);
    
    notesY += isA5 ? 4 : 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(isA5 ? 7 : 8);
    doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);
    const noteLines = doc.splitTextToSize(order.notes, leftColWidth - 4);
    
    const lineHt = Math.max(isA5 ? 8 : 12, noteLines.length * (isA5 ? 3.5 : 4));
    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(margin, notesY - (isA5 ? 2.5 : 3), 1, lineHt, 'F');
    
    let noteLineY = notesY;
    noteLines.forEach((line) => {
      doc.text(line, margin + 3, noteLineY);
      noteLineY += isA5 ? 3.5 : 4;
    });
  }

  const totalsCardWidth = isA5 ? 58 : 65;
  const totalsCardX = pageWidth - margin - totalsCardWidth;
  let totalsY = y;
  
  doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
  doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(totalsCardX, totalsY, totalsCardWidth, totalsCardHeight, 1.5, 1.5, 'FD');
  
  doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.rect(totalsCardX, totalsY, 1.2, totalsCardHeight, 'F');

  totalsY += 4;
  doc.setFontSize(isA5 ? 7.5 : 8);

  const drawTotalsRow = (label, value, isBold = false) => {
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(isBold ? colorPrimary[0] : colorTextDark[0], isBold ? colorPrimary[1] : colorTextDark[1], isBold ? colorPrimary[2] : colorTextDark[2]);
    doc.text(label, totalsCardX + 3, totalsY);
    doc.text(value, pageWidth - margin - 3, totalsY, { align: 'right' });
    totalsY += isA5 ? 4.2 : 5;
  };

  drawTotalsRow('Subtotal:', formatAmount(order.subtotal));
  drawTotalsRow('Discount:', formatAmount(order.discountTotal));
  drawTotalsRow('VAT:', formatAmount(order.vatTotal));
  
  if (Number(order.roundOff)) {
    drawTotalsRow('Round Off:', formatAmount(order.roundOff));
  }

  doc.setFillColor(239, 246, 255);
  doc.rect(totalsCardX + 1.2, totalsY - (isA5 ? 3 : 3.5), totalsCardWidth - 1.5, isA5 ? 5 : 6, 'F');

  doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
  doc.setLineWidth(0.3);
  doc.line(totalsCardX + 3, totalsY - 2, pageWidth - margin - 3, totalsY - 2);
  totalsY += isA5 ? 0.5 : 1;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(isA5 ? 8.5 : 9);
  doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.text('Grand Total:', totalsCardX + 3, totalsY);
  doc.text(formatAmount(Number(order.grandTotal ?? 0) + Number(order.roundOff ?? 0)), pageWidth - margin - 3, totalsY, { align: 'right' });
  totalsY += isA5 ? 5 : 6;

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    if (!isPreprinted) {
      doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
      doc.text(company?.name || '', margin, pageHeight - 8);
      
      doc.text('Thank you for your business!', pageWidth / 2, pageHeight - 8, { align: 'center' });
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    }
  }

  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');
  if (save) {
    doc.save(safeName);
    return undefined;
  }
  return doc.output('arraybuffer');
}

/**
 * Share sales order as PDF.
 */
export async function shareSalesOrderPdf(order, company, options = {}) {
  const filename = (options.filename ?? (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-')).replace(
    /\s+/g,
    '-'
  );
  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');

  const arraybuffer = await exportSalesOrderToPdf(order, company, { ...options, save: false });
  if (!arraybuffer) return;

  await sharePdfArrayBuffer(arraybuffer, {
    filename: safeName,
    title: `Invoice ${order.invoiceNumber || order.orderNumber || ''}`,
    text: company?.name ? `Invoice from ${company.name}` : 'Invoice',
  });
}
